import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * Payment Schema
 * Represents a payment transaction
 */

// Refund sub-schema
export const RefundSchema = z.object({
  amount: z.number().optional(),
  refundDate: z.coerce.date().default(() => new Date()),
  payment: objectId,
  refundId: z.string().optional(),
});

export type Refund = z.infer<typeof RefundSchema>;

export const PaymentSchema = z.object({
  _id: objectId,
  provider: z.string().optional(),
  stripe_payment_intent: z.string().optional(),
  stripe_checkout_session: z.string().optional(),
  currency: z.string().optional(),
  currencyConversionRate: z.number().optional(),
  amount: z.number().optional(),
  // Staty platform fee (cents) taken as a Stripe Connect application fee on
  // this charge. Copied from the checkout metadata by the webhook; 0/absent on
  // exempt orgs, test mode, donations, and historical rows.
  applicationFeeAmount: z.number().optional(),
  donation: z.boolean().optional(),
  isTest: z.boolean().optional(),
  metadata: z.any().optional(), // Mixed type
  refunded: z.boolean().default(false),
  refunds: z.array(RefundSchema).default([]),
  leagueName: z.string().optional(),
  registrationName: z.string().optional(),
  // Actual payer captured from the Stripe checkout session's customer_details
  // (name + email the payer entered at checkout). Distinct from `user`, which is
  // the team owner. Falsy on historical rows / when Stripe didn't collect a name.
  payerName: z.string().optional(),
  payerEmail: z.string().optional(),
  completed: z.boolean().optional(),
  paidDate: z.coerce.date().default(() => new Date()),
  // Refs
  organization: objectId,
  user: objectId,
  registration: objectId,
  registrationTeam: objectId,
  ...timestampFields,
});

export type Payment = z.infer<typeof PaymentSchema>;

// Metadata for Payment fields (for Mongoose refs and UI)
export const paymentMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  provider: { label: 'Provider' },
  stripe_payment_intent: { label: 'Stripe Payment Intent', hidden: true },
  stripe_checkout_session: { label: 'Stripe Checkout Session', hidden: true },
  currency: { label: 'Currency' },
  currencyConversionRate: { label: 'Conversion Rate' },
  amount: { label: 'Amount', editable: true },
  applicationFeeAmount: { label: 'Platform Fee', hidden: true },
  donation: { label: 'Donation' },
  isTest: { label: 'Is Test', hidden: true },
  metadata: { hidden: true },
  refunded: { label: 'Refunded' },
  refunds: { label: 'Refunds' },
  leagueName: { label: 'League Name' },
  registrationName: { label: 'Registration Name' },
  payerName: { label: 'Payer Name' },
  payerEmail: { label: 'Payer Email' },
  completed: { label: 'Completed' },
  paidDate: { label: 'Paid Date' },
  organization: { ref: 'Organization', hidden: true },
  user: { ref: 'User', label: 'User' },
  registration: { ref: 'Registration', label: 'Registration' },
  registrationTeam: { ref: 'RegistrationTeam', label: 'Registration Team' },
  created: { hidden: true },
  updated: { hidden: true },
};

// Metadata for refund sub-document
export const refundMetadata: Record<string, FieldMetadata> = {
  payment: { ref: 'Payment' },
};
